"use client";

import React from "react";
import styled from "styled-components";
import { device } from "../../lib/breakpoints";

const BLACK = "#000000";
const SLATE_800 = "#1e293b";
const WHITE = "#ffffff";
const SAFETY_ORANGE = "#FF5722";
const MONO = 'var(--font-stack-mono, "IBM Plex Mono", ui-monospace, monospace)';
const SANS = "var(--font-stack-sans)";

const STEPS = [
  {
    n: "1",
    title: "Chat & Scope",
    copy:
      "Tell Clumse AI what's broken or what you need built. Uploading photos is completely optional. Our AI handles the heavy lifting by asking the right diagnostic questions to map out your exact project scope upfront.",
  },
  {
    n: "2",
    title: "Local Benchmarks",
    copy:
      "Instantly receive a localized labor and parts cost estimate based on real St. Louis market data. Know what a fair price looks like before you ever talk to a contractor or commit to a search.",
  },
  {
    n: "3",
    title: "Fixed-Price Bids",
    copy:
      "We turn your diagnostic chat into a clean, pre-scoped project blueprint and route it to vetted local pros. Contractors bid accurately without wasting time on site visits, bringing you comparable, stress-free quotes fast.",
  },
];

/**
 * Homepage "How it works" — B2C contractor marketplace (repairs, small fixes, major trades).
 */
export default function ProtocolSequenceSection({ id = "the-protocol" } = {}) {
  return (
    <Section id={id} aria-labelledby="protocol-sequence-heading">
      <Shell>
        <ProtocolHeader id="protocol-sequence-heading">
          How it works
        </ProtocolHeader>

        <StepGrid role="list">
          {STEPS.map((step) => (
            <StepCard key={step.n} role="listitem">
              <StepTitle>
                <StepNum aria-hidden="true">{step.n}.</StepNum> {step.title}
              </StepTitle>
              <StepCopy>{step.copy}</StepCopy>
            </StepCard>
          ))}
        </StepGrid>
      </Shell>
    </Section>
  );
}

const Section = styled.section`
  position: relative;
  z-index: 1;
  padding: 3rem 0;
  background: ${BLACK};
  border-bottom: 1px solid ${SLATE_800};
  width: 100%;
  box-sizing: border-box;
  overflow-x: hidden;

  @media ${device.mobile} {
    padding: 2.75rem 0;
  }
`;

const Shell = styled.div`
  width: 100%;
  max-width: 1536px;
  margin: 0 auto;
  padding-left: clamp(1rem, 4vw, 4rem);
  padding-right: clamp(1rem, 4vw, 4rem);
  box-sizing: border-box;
`;

const ProtocolHeader = styled.h2`
  margin: 0 0 1.35rem;
  text-align: center;
  font-family: var(--font-sans, "Inter", system-ui, sans-serif);
  font-size: clamp(1.35rem, 3.2vw, 1.85rem);
  font-weight: 800;
  letter-spacing: -0.02em;
  line-height: 1.2;
  text-transform: uppercase;
  color: ${WHITE};
`;

const StepGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  width: 100%;
  min-width: 0;
  box-sizing: border-box;

  @media (min-width: 900px) {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
`;

const StepCard = styled.article`
  min-width: 0;
  border: 1px solid ${SLATE_800};
  background: ${BLACK};
  padding: 1rem 1.1rem 1.2rem;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-items: stretch;

  @media (max-width: 899px) {
    &:not(:last-child) {
      border-bottom: none;
    }
  }

  @media (min-width: 900px) {
    padding: 0.95rem 1rem 1.15rem;

    &:not(:last-child) {
      border-right: none;
    }
  }
`;

const StepTitle = styled.h3`
  margin: 0 0 0.55rem;
  padding: 0;
  text-align: center;
  font-family: ${SANS};
  font-size: clamp(0.85rem, 1.4vw, 1rem);
  font-weight: 800;
  letter-spacing: -0.01em;
  text-transform: none;
  color: ${WHITE};
  line-height: 1.3;
  text-wrap: balance;
`;

const StepNum = styled.span`
  font-family: ${MONO};
  font-weight: 800;
  letter-spacing: 0.04em;
  color: ${SAFETY_ORANGE};
`;

const StepCopy = styled.p`
  margin: 0;
  text-align: center;
  font-family: ${SANS};
  font-size: clamp(0.78rem, 1.25vw, 0.88rem);
  line-height: 1.5;
  color: rgba(248, 250, 252, 0.82);
  font-weight: 400;
  min-width: 0;
  overflow-wrap: break-word;
  text-wrap: pretty;
`;
